'use client';

import React, { useEffect, useRef, useState } from 'react';

const BRAND = '#3757FF';

const staffMembers = [
    { name: 'Dr. Ama Mensah', role: 'Head of Mathematics', classes: 6, students: 142, status: 'active', initials: 'AM' },
    { name: 'Mr. Kofi Asante', role: 'English & Literature', classes: 5, students: 118, status: 'active', initials: 'KA' },
    { name: 'Ms. Efua Boateng', role: 'Sciences Department', classes: 7, students: 160, status: 'active', initials: 'EB' },
    { name: 'Mr. Kweku Darko', role: 'ICT & Technology', classes: 4, students: 96, status: 'on-leave', initials: 'KD' },
];

const departments = [
    { name: 'Mathematics', staff: 4, students: 320, color: BRAND },
    { name: 'Sciences', staff: 5, students: 410, color: '#10B981' },
    { name: 'Languages', staff: 3, students: 280, color: '#F59E0B' },
    { name: 'Technology', staff: 2, students: 190, color: '#A855F7' },
];

export default function StudioInstitution() {
    const sectionRef = useRef<HTMLElement>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setVisible(true); }, { threshold: 0.1 });
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <section id="institutions" ref={sectionRef} className="py-28 px-6 md:px-10 relative overflow-hidden" style={{ background: '#fff' }}>
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 80% 20%, rgba(55,87,255,0.04) 0%, transparent 60%)' }} />

            <div className="max-w-7xl mx-auto relative z-10">
                {/* Header */}
                <div className={`mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                    <div
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-700 uppercase tracking-widest mb-6"
                        style={{ background: 'rgba(55,87,255,0.08)', color: BRAND, border: '1px solid rgba(55,87,255,0.15)', letterSpacing: '0.15em' }}
                    >
                        Institution Management
                    </div>
                    <div className="grid lg:grid-cols-2 gap-8 items-end">
                        <h2
                            className="font-display font-800"
                            style={{ fontSize: 'clamp(1.8rem, 3.5vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.025em', color: '#0D0D0D' }}
                        >
                            Run your institution
                            <br />
                            <span style={{ color: BRAND }}>from one place.</span>
                        </h2>
                        <p className="text-base leading-relaxed" style={{ color: '#666', maxWidth: '420px' }}>
                            Manage staff, departments, programs, and institutional identity. Give every educator the tools they need while keeping operations centralized.
                        </p>
                    </div>
                </div>

                {/* Bento grid */}
                <div className={`grid lg:grid-cols-3 gap-5 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                    {/* Staff table — spans 2 cols */}
                    <div className="lg:col-span-2 rounded-3xl p-7" style={{ background: '#FAFAFA', border: '1px solid rgba(55,87,255,0.08)' }}>
                        <div className="flex items-center justify-between mb-6">
                            <p className="text-sm font-800" style={{ color: '#0D0D0D' }}>Staff Directory</p>
                            <span className="text-xs font-600 px-3 py-1.5 rounded-full" style={{ background: 'rgba(55,87,255,0.08)', color: BRAND }}>14 total staff</span>
                        </div>
                        <div className="flex flex-col gap-3">
                            {staffMembers.map((member, i) => (
                                <div
                                    key={member.name}
                                    className="flex items-center gap-4 p-3 rounded-2xl transition-all duration-200 cursor-pointer"
                                    style={{ transitionDelay: `${i * 60}ms` }}
                                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(55,87,255,0.04)'; }}
                                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                                >
                                    <div
                                        className="w-10 h-10 rounded-xl flex items-center justify-center text-xs font-800 flex-shrink-0"
                                        style={{ background: 'rgba(55,87,255,0.1)', color: BRAND }}
                                    >
                                        {member.initials}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-700 truncate" style={{ color: '#0D0D0D' }}>{member.name}</p>
                                        <p className="text-xs font-500" style={{ color: '#888' }}>{member.role}</p>
                                    </div>
                                    <div className="hidden md:flex items-center gap-6 flex-shrink-0">
                                        <div className="text-center">
                                            <p className="text-sm font-800" style={{ color: BRAND }}>{member.classes}</p>
                                            <p className="text-xs font-500" style={{ color: '#AAA' }}>classes</p>
                                        </div>
                                        <div className="text-center">
                                            <p className="text-sm font-800" style={{ color: BRAND }}>{member.students}</p>
                                            <p className="text-xs font-500" style={{ color: '#AAA' }}>students</p>
                                        </div>
                                        <span
                                            className="text-xs font-700 px-2.5 py-1 rounded-full"
                                            style={{
                                                background: member.status === 'active' ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)',
                                                color: member.status === 'active' ? '#10B981' : '#F59E0B',
                                            }}
                                        >
                                            {member.status === 'active' ? 'Active' : 'On leave'}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Departments */}
                    <div className="rounded-3xl p-7" style={{ background: '#FAFAFA', border: '1px solid rgba(55,87,255,0.08)' }}>
                        <p className="text-sm font-800 mb-6" style={{ color: '#0D0D0D' }}>Departments</p>
                        <div className="flex flex-col gap-4">
                            {departments.map((dept) => (
                                <div key={dept.name}>
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="text-sm font-700" style={{ color: '#0D0D0D' }}>{dept.name}</p>
                                        <span className="text-xs font-600" style={{ color: '#888' }}>{dept.staff} staff</span>
                                    </div>
                                    <div className="w-full h-2 rounded-full mb-1" style={{ background: 'rgba(55,87,255,0.08)' }}>
                                        <div
                                            className="h-2 rounded-full transition-all duration-1000"
                                            style={{ width: visible ? `${(dept.students / 450) * 100}%` : '0%', background: dept.color }}
                                        />
                                    </div>
                                    <p className="text-xs font-500" style={{ color: '#AAA' }}>{dept.students} students enrolled</p>
                                </div>
                            ))}
                        </div>

                        <div className="mt-6 pt-5" style={{ borderTop: '1px solid rgba(55,87,255,0.08)' }}>
                            <p className="text-xs font-700 uppercase tracking-widest mb-3" style={{ color: '#999', letterSpacing: '0.12em' }}>Institution Stats</p>
                            <div className="grid grid-cols-2 gap-3">
                                {[
                                    { label: 'Total Students', value: '1,200' },
                                    { label: 'Active Programs', value: '24' },
                                    { label: 'Avg Attendance', value: '91%' },
                                    { label: 'Pass Rate', value: '88%' },
                                ].map(stat => (
                                    <div key={stat.label} className="rounded-xl p-3" style={{ background: 'white', border: '1px solid rgba(55,87,255,0.08)' }}>
                                        <p className="text-base font-800 mb-0.5" style={{ color: BRAND }}>{stat.value}</p>
                                        <p className="text-xs font-500" style={{ color: '#888' }}>{stat.label}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
